/**
 * English catalog — the source of truth for message keys. Its shape (`Messages`)
 * is what every other locale is type-checked against, so adding a key here makes
 * it required everywhere: the build fails until each locale provides it.
 *
 * Keys are flat, dot-namespaced strings (`area.element.role`). Flat keys keep
 * full TypeScript autocomplete on `t('...')` without deep-path type gymnastics.
 * Interpolate dynamic values with `{name}` placeholders and `t(key, { name })`.
 */
export const en = {
  'common.back': 'Back',
  // Appended to links that open a new tab, for screen readers only.
  'common.opensInNewTab': 'opens in a new tab',
  // Omnidek — the protected portal opens top-level in a new tab (see OmnidekPortalLink).
  'omnidek.openPortal': 'Open Omnidek Portal',
  'auth.signedOut': 'You have been signed out.',
  'auth.login': 'Sign in',

  'language.label': 'Select language',

  // Bottom tab bar (labels + nav landmark). Calendar/Training render greyed out
  // and inert until their screens ship — see the tabItems note in TabBar.
  'nav.home': 'Home',
  'nav.calendar': 'Calendar',
  'nav.training': 'Training',
  'nav.profile': 'Profile',
  'nav.primary': 'Primary',

  // In-app splash (React overlay; the static boot splash in index.html has no text).
  'splash.welcome': 'Welcome',
  'splash.loading': 'Loading EDF SuperApp',

  // Home — prospect variant (header greeting, carousel a11y, contact card).
  'home.greeting': 'Welcome back',
  'home.editName': 'Edit name',
  'home.assetId': 'Asset ID',
  'home.carousel.label': 'Highlights',
  'home.carousel.prev': 'Previous',
  'home.carousel.next': 'Next',
  'home.carousel.goToSlide': 'Go to slide {number}',
  'home.carousel.slide1.alt': 'Highlight 1',
  'home.carousel.slide2.alt': 'Highlight 2',
  'home.carousel.slide3.alt': 'Highlight 3',
  'home.contact.title': 'Contact us',
  'home.contact.body':
    'Have a question or need a hand? Our team is here to help — reach out and we’ll get back to you shortly.',
  'home.contact.cta': 'Message Us',
  'home.feature.title': 'Private Capital for Inclusive Economic Growth',
  'home.feature.cta': 'Complete Application',
  'home.apply.cta': 'COMPLETE APPLICATION',
  'home.feature.imageAlt': 'Parents smiling with their young son on his father’s shoulders',

  // Back-navigation bar label (current page title) — reused by drill-in screens.
  'applicationStatus.title': 'Application Status',
  'applicationStatus.overview': 'Overview',
  // Status chips (StatusBadge) — natural-case; the component renders them uppercase.
  'applicationStatus.status.completed': 'Completed',
  'applicationStatus.status.underReview': 'Under Review',
  'applicationStatus.status.draft': 'Draft',
  'applicationStatus.status.actionRequired': 'Action Required',
  'applicationStatus.status.inProgress': 'In Progress',
  // Status card — "Submitted {date}" label ({date} is caller-formatted).
  'applicationStatus.card.nextSteps': 'Next Steps',
  'applicationStatus.card.viewDetails': 'View Details',
  'applicationStatus.card.continue': 'Continue',

  // Application of Interest — drill-in screen embedding the Omnidek form.
  'applicationOfInterest.title': 'Application of Interest',

  // EDA Application — authenticated Form.io upload form.
  'application.title': 'KYC Application',
  'application.loading': 'Loading KYC application...',
  // KYC document upload — structured BFF errors shown on the file field by Form.io.
  'upload.sectionNotOpen': "This section isn't open for uploads yet.",
  'upload.docAlreadyPassed': 'This document has already been approved — no new upload is needed.',
  'upload.documentUnderReview':
    'This document is already under review. If you uploaded the wrong file by mistake, please contact support at support@economicdevelopmentfund.com.',
  'upload.invalidFileType':
    'Only PDF and image files (PNG, JPEG, WebP, GIF, BMP) are supported for in-browser preview. Please upload an accepted format.',
  'upload.fileTooLarge': 'File size exceeds the 10 MB limit. Please upload a smaller file.',
  'upload.budgetNotEditable': 'This budget is under review or already approved.',
  'upload.invoiceNotConfirmed': 'The invoice upload could not be confirmed. Please try again.',
  'upload.invoiceOpenFailed': 'Could not open the invoice. Please try again.',

  // KYC step gating on /apply — what shows instead of (or over) the form.
  'kyc.submitting': 'Submitting your application...',
  'kyc.waiting.title': 'Your documents are being reviewed',
  'kyc.waiting.body':
    "You've uploaded everything we need for now. Our team is reviewing your documents — we'll let you know as soon as there's an update or a new step to complete.",
  'kyc.complete.title': 'Your KYC is complete',
  'kyc.complete.body':
    'All of your documents have been reviewed and approved. There is nothing further to upload.',
  'kyc.failed.title': 'A document needs your attention',
  'kyc.failed.intro':
    'The following was reviewed and could not be accepted. Please upload a replacement.',
  'kyc.failed.noReason': 'No reason was provided. Please upload the document again.',
  'kyc.failed.continue': 'Continue',
  'kyc.submitted.dialog.title': 'Submission Received',
  'kyc.submitted.dialog.body':
    'Your KYC application and documents have been successfully submitted and are currently under review by our team.',
  'kyc.submitted.dialog.viewStatus': 'View Application Status',
  'kyc.submitted.dialog.done': 'Done',
  'kyc.panel.action.status': 'View Application Status',
  'kyc.panel.action.home': 'Back to Home',

  // Profile tab — signed-in identity + sign-out.
  'profile.logout': 'Log out',

  // PWA install banner (shown from the login screen onward, never in prequal).
  // The title renders uppercase via CSS, so it's stored in natural case here.
  'pwa.install.banner.title': 'Get the EDF app',
  'pwa.install.banner.body': 'Faster access and a home-screen shortcut',
  'pwa.install.cta': 'Install App',
  'pwa.install.dismiss': 'Dismiss',

  // Stage Bar — persistent to-do notification.
  'stageBar.important.prefix': 'Important',
  'stageBar.apply.label': 'Complete your application to continue',

  // Next-step reminder dialog (home) — applicant sees the NDA step, participant the KYC step.
  'nextStep.dialog.title': 'Next Steps',
  'nextStep.dialog.nda.body':
    'Thank you for your interest in services provided by The Economic Development Fund. In order to respond to your interest, please complete, sign, and submit the non-disclosure agreement (NDA).',
  'nextStep.dialog.kyc.body':
    'Thank you for your interest in services provided by The Economic Development Fund. In order to continue with your application, please complete and submit the Know Your Customer (KYC) form.',
  'nextStep.dialog.close': 'Close',
  'nextStep.dialog.next': 'Next',
}

export type Messages = typeof en
export type MessageKey = keyof Messages
